import React, { useState, useCallback } from 'react';
import Cropper from 'react-easy-crop';
import { Dialog } from 'primereact/dialog';

interface PhotoCropperProps {
  visible: boolean;
  onHide: () => void;
  imageSrc: string;
  onCropComplete: (croppedImage: Blob) => void;
}

const PhotoCropper: React.FC<PhotoCropperProps> = ({
  visible,
  onHide,
  imageSrc,
  onCropComplete
}) => {
  const [crop, setCrop] = useState({ x: 0, y: 0 });
  const [zoom, setZoom] = useState(1);
  const [croppedAreaPixels, setCroppedAreaPixels] = useState(null);

  const onCropCompleteHandler = useCallback((croppedArea: any, croppedAreaPixels: any) => {
    setCroppedAreaPixels(croppedAreaPixels);
  }, []);

  const createImage = (url: string): Promise<HTMLImageElement> =>
    new Promise((resolve, reject) => {
      const image = new Image();
      image.addEventListener('load', () => resolve(image));
      image.addEventListener('error', error => reject(error));
      image.setAttribute('crossOrigin', 'anonymous');
      image.src = url;
    });

  const getCroppedImg = async (imageSrc: string, pixelCrop: any): Promise<Blob> => {
    const image = await createImage(imageSrc);
    const canvas = document.createElement('canvas');
    const ctx = canvas.getContext('2d');

    if (!ctx) {
      throw new Error('No 2d context');
    }

    canvas.width = pixelCrop.width;
    canvas.height = pixelCrop.height;

    ctx.drawImage(
      image,
      pixelCrop.x,
      pixelCrop.y,
      pixelCrop.width,
      pixelCrop.height,
      0,
      0,
      pixelCrop.width,
      pixelCrop.height
    );

    return new Promise((resolve) => {
      canvas.toBlob((blob) => {
        resolve(blob as Blob);
      }, 'image/jpeg', 0.8);
    });
  };

  const handleCrop = async () => {
    if (croppedAreaPixels) {
      try {
        const croppedImage = await getCroppedImg(imageSrc, croppedAreaPixels);
        onCropComplete(croppedImage);
        onHide();
      } catch (e) {
        console.error(e);
      }
    }
  };

  return (
    <Dialog
      visible={visible}
      onHide={onHide}
      header="Crop Photo"
      style={{ width: '500px', height: '600px' }}
      modal
    >
      <div style={{ position: 'relative', width: '100%', height: '400px' }}>
        <Cropper
          image={imageSrc}
          crop={crop}
          zoom={zoom}
          aspect={1}
          cropShape="round"
          showGrid={false}
          onCropChange={setCrop}
          onCropComplete={onCropCompleteHandler}
          onZoomChange={setZoom}
        />
      </div>
      <div className="tw-mt-4 tw-flex tw-justify-between tw-items-center">
        <div className="tw-flex tw-items-center tw-gap-2">
          <label>Zoom:</label>
          <input
            type="range"
            value={zoom}
            min={1}
            max={3}
            step={0.1}
            onChange={(e) => setZoom(Number(e.target.value))}
            className="tw-w-32"
          />
        </div>
        <div className="tw-space-x-2">
          <button
            type="button"
            onClick={onHide}
            className="ia-btn ia-btn-outline-primary px-4"
          >
            Cancel
          </button>
          <button
            type="button"
            onClick={handleCrop}
            className="ia-btn ia-btn-primary px-4"
          >
            Crop & Save
          </button>
        </div>
      </div>
    </Dialog>
  );
};

export default PhotoCropper;